<template>
<div class="suggestion" :class="{ 'is-notification': suggestion.isNotification }">
    <SuggestionStatus :suggestion="suggestion" />
    <SuggestionHeader
        :suggestion="suggestion"
        :isAllowedToEmote="canAddNewEmote"/>
    <div class="pre-wrap-text">{{ suggestion.text }}</div>
    <LinkPreview
        v-if="suggestion.linkPreview"
        :linkPreview="suggestion.linkPreview"
        :revealLinkPreview="revealLinkPreview" />
    <SuggestionReply
        v-if="suggestion.reply"
        :user="broadcasterUser"
        :reply="suggestion.reply"/>
    <transition-group
        class="reactions"
        name="reactions"
        tag="div"
        ref="reactions"
        @before-enter="beforeEnter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        appear>
        <Vote
            :suggestion="suggestion"
            :broadcasterUser="broadcasterUser"
            :broadcasterUpvoted="broadcasterUpvoted"
            key="vote"/>
        <SuggestionCompleted
            v-if="suggestion.completedAt"
            :suggestion="suggestion"
            :isBroadcaster="isBroadcaster"
            key="completed"/>
        <SuggestionBounty
            v-if="showBounty"
            :suggestion="suggestion"
            key="bounty"/>
        <EmoteReaction
            v-for="emoteReaction in displayedEmotes"
            :emoteReaction="emoteReaction"
            :suggestion="suggestion"
            :canIncrementExistingEmote="canIncrementExistingEmote"
            :key="emoteReaction.emote"/>
        <span
            v-if="numHiddenEmotes > 0"
            @click="showAll = true"
            class="show-more emote-reaction"
            key="more">
            {{ numHiddenEmotes.toLocaleString() }} more
        </span>
    </transition-group>
</div>
</template>

<script>
import { mapGetters } from 'vuex'

import * as C from '@shared/constants'
import * as _ from '@/lodash'
import tippy, { createSingleton } from '@/tippy'
import SuggestionStatus from './SuggestionStatus'
import SuggestionHeader from './SuggestionHeader'
import SuggestionReply from './SuggestionReply'
import LinkPreview from '@/components/LinkPreview'
import Vote from '@/components/viewer/Vote'
import SuggestionCompleted from '@/components/viewer/SuggestionCompleted'
import SuggestionBounty from '@/components/viewer/bounty/SuggestionBounty'
import EmoteReaction from '@/components/emotes/EmoteReaction'

export default {
    name: 'Suggestion',
    props: {
        suggestion: Object,
    },
    data: () => ({
        showAll: false,
    }),
    computed: {
        ...mapGetters([
            'isBroadcaster',
            'broadcasterUser',
            'revealLinkPreview',
            'isBitsEnabled',
            'bitsProducts',
        ]),
        emotes: vm => vm.suggestion.emotes,
        displayedEmotes: vm => {
            const { emotes, showAll } = vm
            const num = showAll ? emotes.length : 10
            return _.orderBy(vm.emotes, ['count'], ['desc']).slice(0, num)
        },
        numHiddenEmotes: vm => vm.emotes.length - vm.displayedEmotes.length,
        numEmotesByUser: vm => {
            return vm.emotes.reduce((memo, e) => memo + (e.userReacted ? 1 : 0), 0)
        },
        canIncrementExistingEmote (vm) {
            return vm.numEmotesByUser < C.MAX_EMOTES_PER_SUGG_PER_USER
        },
        canAddNewEmote (vm) {
            return vm.canIncrementExistingEmote &&
                vm.emotes.length < C.MAX_UNIQUE_EMOTES_PER_SUGG
        },
        broadcasterUpvoted: vm => vm.suggestion.broadcasterVoteType === C.VOTE_UP,
        showBounty: vm => vm.isBitsEnabled && vm.bitsProducts.length > 0,
    },
    watch: {
        // TODO: This is annoying. Find a library that uses popper 2.0?
        showAll: 'updateTippy',
        suggestion: {
            handler: 'updateTippy',
            deep: true,
        },
    },
    mounted () {
        this.updateTippy()
        this.$once('hook:destroyed', this.destroyTippy)
    },
    methods: {
        async updateTippy () {
            this.destroyTippy()
            await this.$nextTick() // wait for rerender.

            const selector = '[data-tippy-content]'
            const $targets = this.$refs.reactions.$el.querySelectorAll(selector)

            this.tippyInstances = tippy($targets)
            this.singleton = createSingleton(this.tippyInstances, {
                appendTo: this.$el,
                //trigger: 'click', // DEBUG ONLY
            })
        },
        destroyTippy () {
            /* eslint-disable no-unused-expressions */ // fuck off eslint wtf
            this.tippyInstances?.forEach(t => t.destroy())
            this.singleton?.destroy()
        },
        beforeEnter ($el) {
            // Only delay the emote reactions.
            if (!$el.classList.contains('emote-reaction')) {
                return
            }
            const delay = randomInRange(0, 200)
            $el.style.transitionDelay = `${delay}ms`
        },
        afterEnter ($el) {
            $el.style.transitionDelay = ''
        },
        beforeLeave ($el) {
            // Leave in place.
            const top = $el.offsetTop + 'px'
            const left = $el.offsetLeft + 'px'
            // assign all at once to prevent unnecessary reflows
            Object.assign($el.style, { left, top })
        },
        onImgErr (e) {
            this.hasImgErr = true
        }
    },
    components: {
        SuggestionStatus,
        SuggestionHeader,
        SuggestionReply,
        LinkPreview,
        Vote,
        SuggestionCompleted,
        SuggestionBounty,
        EmoteReaction,
    }
}

const randomInRange = (min, max) => Math.random() * (max - min) + min
</script>

<style lang="scss" scoped>
.suggestion {
    position: relative;
    display: grid;
    grid-gap: 10px;
    padding: 8px;
    transition: background-color $default-transition-duration;
    word-wrap: break-word;
    border-bottom: 1px solid $border-color;
    &.is-notification {
        // TODO: Highlight me somehow
    }
}

.reactions {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: start;
    align-items: center;
    // override any custom font with the bulma default for a consistent UI.
    font-family: $family-sans-serif;
    > .icon {
        margin-right: 4px;
    }
}

// No transition for the vote icons, it's too much.
.reactions-enter-active.vote {
    transition: none;
}

.reactions-enter-active {
    // Bouncy animation
    transition: $emote-transition-duration cubic-bezier(.34,1.56,.95,.89);
}

.reactions-leave-active {
    transition-duration: 0.1s;
    position: absolute;
}

.reactions-move {
    transition: $emote-transition-duration;
}

.reactions-enter, .reactions-leave-to {
    transform: scale(0);
}
</style>
